import { useState } from "react";
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ClipboardCheck,
  Factory,
  FileSearch,
  Landmark,
  LineChart,
  Shield,
  Truck,
  Users,
  type LucideIcon,
} from "lucide-react";
import LeadCaptureModal from "../components/LeadCaptureModal";
import type { LeadRequestType } from "../services/lead-capture-api";

// A CTA is either a lead-capture form (opens the modal) or a plain link (e.g. internal nav).
type CtaAction =
  | { kind: "lead"; requestType: LeadRequestType; modalTitle: string }
  | { kind: "link"; href: string };

type LandingPageConfig = {
  eyebrow: string;
  title: string;
  subtitle: string;
  body: string;
  primaryCta: string;
  primaryAction: CtaAction;
  secondaryCta: string;
  secondaryAction: CtaAction;
  Icon: LucideIcon;
  proof: string[];
  sections: {
    title: string;
    description: string;
    Icon: LucideIcon;
  }[];
  closingTitle: string;
  closingBody: string;
};

const PAGES: Record<string, LandingPageConfig> = {
  logistics: {
    eyebrow: "For UK and European logistics teams",
    title: "Logistics Compliance Automation",
    subtitle:
      "Find shipment evidence gaps before they become customs delays, finance disputes, or audit issues.",
    body:
      "LightLink AI gives freight forwarders, logistics operators, customs-linked teams, and regulated transport workflows one control tower for VAT, EORI, eCMR, and audit readiness.",
    primaryCta: "Request a Pilot Review",
    primaryAction: {
      kind: "lead",
      requestType: "pilot_review",
      modalTitle: "Request a Pilot Review",
    },
    secondaryCta: "Book a Compliance Readiness Demo",
    secondaryAction: {
      kind: "lead",
      requestType: "demo",
      modalTitle: "Book a Compliance Readiness Demo",
    },
    Icon: Truck,
    proof: [
      "Shipment evidence completeness review",
      "VAT, EORI, eCMR, and audit readiness",
      "Exception ownership across operations and compliance",
    ],
    sections: [
      {
        title: "Document Evidence",
        description:
          "Review shipment files for missing records, inconsistent data, and audit exposure.",
        Icon: FileSearch,
      },
      {
        title: "Registry Checks",
        description:
          "Support VAT, EORI, sanctions, and compliance registry workflows in one place.",
        Icon: Landmark,
      },
      {
        title: "Case Workflow",
        description:
          "Move exceptions from inboxes and spreadsheets into trackable review queues.",
        Icon: ClipboardCheck,
      },
    ],
    closingTitle: "A better route from shipment documents to compliance evidence.",
    closingBody:
      "Use this page as the destination for SEO, LinkedIn awareness, and retargeting campaigns aimed at logistics operators and freight teams.",
  },
  manufacturing: {
    eyebrow: "For manufacturers with internal logistics",
    title: "Manufacturing Logistics Compliance",
    subtitle:
      "Bring shipment evidence, supplier transport records, VAT checks, and audit history into one review workflow.",
    body:
      "LightLink AI helps manufacturers with internal logistics operations coordinate logistics, finance, and compliance teams before shipment issues reach customers, customs, or auditors.",
    primaryCta: "Book a Compliance Readiness Demo",
    primaryAction: {
      kind: "lead",
      requestType: "demo",
      modalTitle: "Book a Compliance Readiness Demo",
    },
    secondaryCta: "Request a Pilot Review",
    secondaryAction: {
      kind: "lead",
      requestType: "pilot_review",
      modalTitle: "Request a Pilot Review",
    },
    Icon: Factory,
    proof: [
      "Internal logistics operations readiness",
      "Supplier transport records and shipment evidence review",
      "Finance, compliance, and operations coordination",
    ],
    sections: [
      {
        title: "Supplier Movement Evidence",
        description:
          "Review supplier transport records and shipment documentation before exceptions spread.",
        Icon: FileSearch,
      },
      {
        title: "Finance Alignment",
        description:
          "Connect VAT, invoice, registry, and shipment evidence checks for cleaner handoffs.",
        Icon: BarChart3,
      },
      {
        title: "Operational Audit Trail",
        description:
          "Keep review decisions, ownership, and evidence history ready for audit conversations.",
        Icon: Shield,
      },
    ],
    closingTitle: "Designed for manufacturers that operate logistics, not only buy freight.",
    closingBody:
      "Use this page for manufacturing buyer campaigns, founder-led outreach, and pilot follow-up links.",
  },
  partners: {
    eyebrow: "Partner channel",
    title: "Partner with LightLink AI",
    subtitle:
      "Help logistics and manufacturing clients improve shipment evidence readiness without adding manual review burden.",
    body:
      "LightLink AI is built for customs advisors, freight technology providers, and compliance consultancies that want a practical AI workflow for document evidence, registry checks, exceptions, and audit trails.",
    primaryCta: "Start a Partner Conversation",
    primaryAction: {
      kind: "lead",
      requestType: "partner",
      modalTitle: "Start a Partner Conversation",
    },
    secondaryCta: "View Pilot Offer",
    secondaryAction: { kind: "link", href: "/pilot" },
    Icon: Users,
    proof: [
      "Referral and implementation partner fit",
      "Useful alongside customs, freight, ERP, and compliance advisory work",
      "Pilot-led path before larger rollout conversations",
    ],
    sections: [
      {
        title: "Advisory Partners",
        description:
          "Give customs and compliance clients a practical workflow for evidence readiness.",
        Icon: ClipboardCheck,
      },
      {
        title: "Technology Partners",
        description:
          "Pair operational systems with AI-assisted review, exceptions, and audit history.",
        Icon: LineChart,
      },
      {
        title: "Pilot Referrals",
        description:
          "Use the 30-day readiness pilot as a controlled first step for qualified accounts.",
        Icon: CheckCircle2,
      },
    ],
    closingTitle: "A practical partner motion for logistics compliance operations.",
    closingBody:
      "Use this page when approaching advisors, freight platforms, consultants, and channel partners.",
  },
  investors: {
    eyebrow: "Company credibility",
    title: "Investor Overview",
    subtitle:
      "LightLink AI is building an AI compliance control tower for UK and European logistics compliance operations.",
    body:
      "The product focuses on a specific operational wedge: shipment evidence, VAT and registry checks, exception workflow, and audit-ready records for logistics and manufacturing teams.",
    primaryCta: "Request Investor Materials",
    primaryAction: {
      kind: "lead",
      requestType: "investor",
      modalTitle: "Request Investor Materials",
    },
    secondaryCta: "Book Product Demo",
    secondaryAction: {
      kind: "lead",
      requestType: "demo",
      modalTitle: "Book a Compliance Readiness Demo",
    },
    Icon: LineChart,
    proof: [
      "Focused UK and European logistics compliance market",
      "Pilot-led commercial motion for early proof",
      "Product surface already aligned to document, registry, case, and audit workflows",
    ],
    sections: [
      {
        title: "Market Wedge",
        description:
          "Start with shipment evidence readiness where logistics teams already feel manual review pain.",
        Icon: Truck,
      },
      {
        title: "Product Depth",
        description:
          "Document intelligence, registry checks, risk workflow, cases, and audit reporting in one portal.",
        Icon: Shield,
      },
      {
        title: "Commercial Motion",
        description:
          "Demo bookings, 30-day pilots, partner referrals, and focused outbound into target accounts.",
        Icon: BarChart3,
      },
    ],
    closingTitle: "Specific, operational, and built around measurable pilot outcomes.",
    closingBody:
      "Use this page for investor follow-up, advisor introductions, and partner credibility conversations.",
  },
};

type MarketingLandingPageProps = {
  page: keyof typeof PAGES;
};

function CtaButton({
  action,
  onOpenLead,
  className,
  children,
}: {
  action: CtaAction;
  onOpenLead: (requestType: LeadRequestType, modalTitle: string) => void;
  className: string;
  children: React.ReactNode;
}) {
  if (action.kind === "link") {
    return (
      <a href={action.href} className={className}>
        {children}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpenLead(action.requestType, action.modalTitle)}
      className={className}
    >
      {children}
    </button>
  );
}

export default function MarketingLandingPage({ page }: MarketingLandingPageProps) {
  const config = PAGES[page];
  const HeroIcon = config.Icon;
  const [leadModal, setLeadModal] = useState<{
    requestType: LeadRequestType;
    title: string;
  } | null>(null);

  function openLead(requestType: LeadRequestType, title: string) {
    setLeadModal({ requestType, title });
  }

  return (
    <main className="min-h-screen bg-[#050814] text-white">
      <LeadCaptureModal
        open={leadModal !== null}
        onClose={() => setLeadModal(null)}
        requestType={leadModal?.requestType ?? "demo"}
        title={leadModal?.title ?? "Get in touch"}
        description="Tell us a bit about your team and we'll get back to you shortly."
      />
      <section className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-8 sm:px-6 lg:px-8">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_18%_8%,rgba(56,189,248,0.16),transparent_30%),radial-gradient(circle_at_82%_18%,rgba(16,185,129,0.1),transparent_24%),linear-gradient(135deg,rgba(15,23,42,0.97),rgba(2,6,23,1))]" />

        <header className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-5">
          <a href="/" className="text-lg font-bold text-white">
            LightLink AI
          </a>
          <nav className="flex flex-wrap gap-3 text-sm font-semibold">
            <a className="text-slate-300 hover:text-sky-200" href="/pilot">
              Pilot
            </a>
            <a className="text-slate-300 hover:text-sky-200" href="/partners">
              Partners
            </a>
            <a className="text-slate-300 hover:text-sky-200" href="/investors">
              Investors
            </a>
          </nav>
        </header>

        <div className="grid flex-1 items-center gap-8 py-10 lg:grid-cols-[1fr_0.92fr]">
          <section className="space-y-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-300/25 bg-sky-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-sky-200">
              <HeroIcon size={14} />
              {config.eyebrow}
            </div>

            <div className="space-y-5">
              <h1 className="max-w-4xl text-4xl font-bold leading-tight text-white sm:text-5xl">
                {config.title}
              </h1>
              <p className="max-w-3xl text-xl font-semibold leading-8 text-slate-200">
                {config.subtitle}
              </p>
              <p className="max-w-3xl text-sm leading-7 text-slate-400 sm:text-base">
                {config.body}
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <CtaButton
                action={config.primaryAction}
                onOpenLead={openLead}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-sky-950/30 transition-colors hover:bg-sky-400"
              >
                {config.primaryCta}
                <ArrowRight size={16} />
              </CtaButton>
              <CtaButton
                action={config.secondaryAction}
                onOpenLead={openLead}
                className="inline-flex items-center justify-center rounded-lg border border-white/15 bg-white/10 px-5 py-3 text-sm font-semibold text-slate-100 transition-colors hover:border-sky-300/50 hover:bg-sky-300/10"
              >
                {config.secondaryCta}
              </CtaButton>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {config.proof.map((item) => (
                <div
                  key={item}
                  className="rounded-lg border border-white/10 bg-white/[0.045] px-4 py-3 text-sm leading-6 text-slate-300"
                >
                  <CheckCircle2 className="mb-2 text-emerald-300" size={18} />
                  {item}
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-sky-300/25 bg-slate-950/80 p-5 shadow-2xl shadow-sky-950/30">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-sky-200">
              Campaign destination
            </p>
            <h2 className="mt-2 text-2xl font-bold text-white">
              What buyers can evaluate
            </h2>

            <div className="mt-5 grid gap-3">
              {config.sections.map(({ title, description, Icon }) => (
                <article
                  key={title}
                  className="rounded-xl border border-white/10 bg-white/[0.045] p-4"
                >
                  <div className="flex gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-400/15 text-sky-200">
                      <Icon size={19} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white">
                        {title}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-slate-400">
                        {description}
                      </p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-5 rounded-xl border border-emerald-300/20 bg-emerald-300/10 p-4 text-sm leading-6 text-emerald-50">
              <p className="font-semibold text-white">{config.closingTitle}</p>
              <p className="mt-2 text-emerald-100">{config.closingBody}</p>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}
